import { useContext } from 'react'
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json'
import { BuilderCreateModal } from './BuilderCreateModal'
import { BuilderTemplatePicker } from './BuilderTemplatePicker'

/**
 * `BuilderPageCreateMount` — l'enveloppe reactive-json des écrans de création de
 * page du builder, pour qu'ils s'ouvrent SUR l'écran courant.
 *
 * Sans elle, les boutons de `/sites/:id` étaient des liens vers
 * `/builder?create=…` : l'éditeur quittait l'écran du site pour l'accueil du
 * Page Builder, et découvrait la pop-in par-dessus une liste qu'il n'avait pas
 * demandée. La création est un geste qu'on fait *depuis* un écran, pas une
 * destination.
 *
 * Ce fichier n'existe que parce que ces composants sont à CALLBACKS
 * (`onClose`) : reactive-json passe des valeurs, jamais des fonctions, d'où
 * l'adaptateur. Même montage que `TaskCreateModal` autour de `NewTaskModal` —
 * règle maison : on réutilise l'écran générique, on ne le redessine pas.
 *
 * UN seul montage pour les trois gestes : c'est `mode` qui décide lequel
 * s'ouvre, et le refermer revient à vider ce même chemin. Trois montages
 * distincts obligeraient à trois booléens dans le store, tous à remettre à faux
 * à chaque ouverture.
 *
 * Props :
 *   - mode      : `blank` | `template` | `new-template` (chemin évalué).
 *                 Vide / absent ⇒ rien n'est monté (donc aucun chargement).
 *   - modePath  : le même chemin, brut, pour refermer.
 *   - siteId    : le site auquel rattacher la page créée.
 *   - directoryId : le répertoire du WIKI où ranger la page créée — exclusif de
 *                 `siteId`. C'est ce que passe l'écran d'un espace documentaire
 *                 (`/docs/space/:tid`), qui offre les MÊMES trois gestes.
 *   - site      : le site lui-même, dont on lit `defaultModelNid` pour
 *                 présélectionner son modèle par défaut. Enveloppe Syncable
 *                 (`{data:{…}}`) pour qui peut l'écrire, objet plat sinon : les
 *                 deux formes cohabitent selon le lecteur, on tolère les deux.
 */
export const BuilderPageCreateMount = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const ev = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const mode = String(ev(props.mode) ?? '')
  const modePath = String(props.modePath ?? '').replace(/^~~?\./, '')
  const siteId = ev(props.siteId) ?? null
  const directoryId = ev(props.directoryId) ?? null
  const rawSite = ev(props.site)
  const site = (rawSite && rawSite.data) ? rawSite.data : (rawSite || {})

  const close = () => { if (modePath) globalDataContext.updateData('', modePath) }

  if (mode === 'template') {
    return (
      <BuilderTemplatePicker
        onClose={close}
        siteId={siteId}
        directoryId={directoryId}
        // Entier côté back (`defaultModelNid`, calculé) — le CHAMP, lui, sort en
        // chaîne et ne s'apparierait pas au `nid` de /api/builder/pages.
        defaultModelNid={site.defaultModelNid ?? null}
      />
    )
  }
  if (mode === 'blank' || mode === 'new-template') {
    return (
      <BuilderCreateModal
        onClose={close}
        siteId={siteId}
        directoryId={directoryId}
        asTemplate={mode === 'new-template'}
        defaultModelNid={site.defaultModelNid ?? null}
      />
    )
  }
  return null
}

export default BuilderPageCreateMount
